import React, { useState } from "react";

const MI_TO_KM = 1.60934;

export default function PaceCalc() {
  const [distance, setDistance] = useState("");
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [unit, setUnit] = useState("miles");
  const [pace, setPace] = useState(null);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const calculatePace = (distanceValue, hoursValue, minutesValue) => {
    const d = parseFloat(distanceValue);
    const h = parseInt(hoursValue, 10) || 0;
    const m = parseInt(minutesValue, 10) || 0;
    const totalMinutes = h * 60 + m;

    if (Number.isNaN(d) || d <= 0) {
      setError("Please enter a valid distance.");
      setPace(null);
      return false;
    }

    if (totalMinutes <= 0) {
      setError("Please enter a valid time.");
      setPace(null);
      return false;
    }

    const paceMin = totalMinutes / d;
    let paceMinPart = Math.floor(paceMin);
    let paceSecPart = Math.round((paceMin - paceMinPart) * 60);
    if (paceSecPart >= 60) {
      paceMinPart += 1;
      paceSecPart = 0;
    }

    setError("");
    setPace({ paceMinPart, paceSecPart });
    return true;
  };

  const handleCalculate = () => {
    setSubmitted(true);
    calculatePace(distance, hours, minutes);
  };

  const handleUnitChange = (e) => {
    const nextUnit = e.target.value;
    const currentDistance = parseFloat(distance);
    let convertedDistance = distance;

    if (!Number.isNaN(currentDistance) && currentDistance > 0) {
      const converted =
        unit === "miles" && nextUnit === "km"
          ? currentDistance * MI_TO_KM
          : unit === "km" && nextUnit === "miles"
            ? currentDistance / MI_TO_KM
            : currentDistance;

      convertedDistance = converted.toFixed(2);
      setDistance(convertedDistance);
    }

    setUnit(nextUnit);
    setSubmitted(true);
    calculatePace(convertedDistance, hours, minutes);
  };

  const unitLabel = unit === "km" ? "km" : "mi";

  return (
    <article className="pw-calc-card">
      <h2 className="pw-calc-card-title">Pace</h2>
      <p className="pw-calc-card-lead">
        Enter distance and time to see your average pace per mile or kilometer.
      </p>

      <div className="pw-calc-fields">
        <div className="pw-calc-field">
          <label htmlFor="pace-distance" className="pw-calc-label">
            Distance
          </label>
          <input
            id="pace-distance"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            placeholder={unit === "km" ? "e.g. 10" : "e.g. 6.21 (10K)"}
            className="pw-calc-input"
            value={distance}
            onChange={(e) => setDistance(e.target.value)}
          />
        </div>
        <div className="pw-calc-field">
          <label htmlFor="pace-hours" className="pw-calc-label">
            Hours
          </label>
          <input
            id="pace-hours"
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="0"
            className="pw-calc-input"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
          />
        </div>
        <div className="pw-calc-field">
          <label htmlFor="pace-minutes" className="pw-calc-label">
            Minutes
          </label>
          <input
            id="pace-minutes"
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="0"
            className="pw-calc-input"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
          />
        </div>
        <div className="pw-calc-field">
          <label htmlFor="pace-unit" className="pw-calc-label">
            Unit
          </label>
          <select
            id="pace-unit"
            className="pw-calc-select"
            value={unit}
            onChange={handleUnitChange}
          >
            <option value="miles">Miles</option>
            <option value="km">Kilometers</option>
          </select>
        </div>
      </div>

      <p
        style={{
          marginTop: "0.75rem",
          color: "var(--home-muted, #a8a8a8)",
          fontSize: "0.92rem",
          lineHeight: 1.5,
        }}
      >
        Example: You ran 3.5 miles in 27 minutes. Enter 3.5 for distance, 0
        hours, 27 minutes, select Miles, and hit Calculate. Your pace would be
        7:42 / mi.
      </p>

      <button
        type="button"
        className="pw-calc-submit"
        onClick={handleCalculate}
      >
        Calculate
      </button>

      <div className="pw-calc-results" aria-live="polite">
        {!submitted && (
          <p className="pw-calc-results-placeholder">
            Your pace will appear here after you tap Calculate.
          </p>
        )}
        {submitted && error && (
          <p className="pw-calc-results-error">{error}</p>
        )}
        {submitted && !error && pace != null && (
          <>
            <span className="pw-calc-results-label">Result</span>
            <p className="pw-calc-results-value">
              Pace:{" "}
              <strong>
                {pace.paceMinPart}:
                {pace.paceSecPart.toString().padStart(2, "0")} / {unitLabel}
              </strong>
            </p>
          </>
        )}
      </div>
    </article>
  );
}
